import React, { useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Checkbox, Form } from 'antd'
import { useTranslation } from 'lib/i18n'
import { useDebounce } from 'use-debounce'

import ExtendedInstructionsAddon from 'components/ProductPage/shared/ExtendedInstructionsAddon'
import ExtendedInstructionsAddonBusiness from 'components/ProductPage/shared/ExtendedInstructionsAddonBusiness'
import { VIDEO_REQUEST_INSTRUCTIONS } from 'components/ProductPage/shared/FieldConstants'
import TextAreaWithSuggestions from 'components/TextAreaWithSuggestions/TextAreaWithSuggestions'
import useDetectLanguageQuery from 'hooks/useDetectLanguage.gql'
import useLangMap from 'hooks/useLangMap'

import styles from './Instructions.module.css'

const MAX_INSTRUCTIONS_COUNT = 250
const MAX_INSTRUCTIONS_COUNT_WITH_ADDON = 600

export default function Instructions({
  profile,
  extendedInstructionsActive,
  extendedInstructionsProduct,
  toggleExtendedInstructions,
  profileProduct,
  showSavedIndicator,
  form,
  isMarketingProduct = false,
  placeholder = '',
}) {
  const isBusinessProductInternal =
    profileProduct?.product?.productType === 'VIDEO_REQUEST_BUSINESS_INTERNAL'

  const { t } = useTranslation()
  const maxInstructionsCount = extendedInstructionsActive
    ? MAX_INSTRUCTIONS_COUNT_WITH_ADDON
    : MAX_INSTRUCTIONS_COUNT

  const fieldName = VIDEO_REQUEST_INSTRUCTIONS
  const INPUT_MIN_LENGTH = 50
  const MIN_DETECT_LENGTH = 35

  useEffect(() => {
    const instructionsText = form.getFieldValue(fieldName)

    if (instructionsText && instructionsText.length > 0) {
      form.validateFields([fieldName])
    }
  }, [extendedInstructionsActive, fieldName, form])

  const { getFullNameForLanguage } = useLangMap()

  const [detectLanguage, { data = {}, loading }] = useLazyQuery(
    useDetectLanguageQuery,
  )
  const [term, setTerm] = useState('')
  const [debouncedTerm] = useDebounce(term, 400)
  const [languageWarning, setLanguageWarning] = useState({})

  useEffect(() => {
    if (debouncedTerm?.length >= MIN_DETECT_LENGTH) {
      detectLanguage({
        variables: { text: debouncedTerm },
      })
    }
  }, [debouncedTerm, detectLanguage])

  const { name: profileName, id: profileId, languagesSpoken } = profile

  const initialValue = { id: [], label: [] }
  const profileLanguages =
    languagesSpoken?.reduce((acc, lgn) => {
      acc.id.push(lgn.id)
      acc.label.push(lgn.label)
      return acc
    }, initialValue) || initialValue

  const inputLanguage = (_rule, value) => {
    setTerm(value)
    const language = getFullNameForLanguage(profileLanguages.label)
    const detectedLang = data?.detectLanguage?.language

    if (
      !loading &&
      profileLanguages.id.length &&
      term?.length >= INPUT_MIN_LENGTH &&
      !profileLanguages.id.includes(detectedLang)
    ) {
      if (value.length === 0) {
        setTerm('')
        return Promise.resolve()
      }

      setLanguageWarning({
        validateStatus: 'warning',
        help: t(
          'validators.languageDetector.unSupportedLanguageForTalent',
          'Please note that this talent only make videos in {{language}}',
          { language },
        ),
      })

      return Promise.resolve()
    }

    if (Object.keys(languageWarning).length) {
      setLanguageWarning({})
    }

    return Promise.resolve()
  }
  const ExtendedInstructionsAddonComp = isBusinessProductInternal
    ? ExtendedInstructionsAddonBusiness
    : ExtendedInstructionsAddon
  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue, setFieldsValue, validateFields, getFieldError }) => {
        const charactersUsed = getFieldValue(fieldName)
          ? getFieldValue(fieldName).length
          : 0
        const charactersLeft = maxInstructionsCount - charactersUsed
        const hasError = !!getFieldError(fieldName).length

        return (
          <>
            <Form.Item
              label={
                isMarketingProduct
                  ? t(
                      'BusinessVideoRequestForm.instructionsLabel',
                      'What do you want them to do or say in the video?',
                    )
                  : t(
                      'VideoRequestForm.instructionsLabel2',
                      'Instructions to {{profileName}}.',
                      {
                        profileName,
                        charCount: charactersLeft,
                      },
                    )
              }
              extra={
                <div className={styles.instructionsExtraInfo}>
                  {t(
                    'VideoRequestForm.charactersRemaining',
                    '{{charCount}} characters remaining',
                    {
                      charCount: charactersLeft,
                    },
                  )}
                  {showSavedIndicator && (
                    <div data-cy="instructionsSavedText">
                      {t('VideoRequestForm.instructionsSaved', 'Saved!')}
                    </div>
                  )}
                </div>
              }
              shouldUpdate
              name={fieldName}
              {...(hasError ? {} : languageWarning)}
              validateTrigger={['onChange', 'onSubmit']}
              rules={[
                {
                  required: true,
                  message: t(
                    'VideoRequestForm.instructionsRequired',
                    'You need to fill in the instructions',
                  ),
                },
                {
                  min: 20,
                  message: t(
                    'VideoRequestForm.instructionsTooShort',
                    'Great start! Add some more instructions (min 20 characters)',
                  ),
                },
                {
                  max: maxInstructionsCount,
                  message: t(
                    'VideoRequestForm.instructionsTooLong2',
                    'Your instructions are too long - keep it to below {{count}} characters',
                    { count: maxInstructionsCount },
                  ),
                },
                {
                  validator: inputLanguage,
                  validateTrigger: 'onChange',
                },
              ]}
              data-cy="instructions"
            >
              <TextAreaWithSuggestions
                hideSuggestions={isMarketingProduct}
                onChangeSuggestion={(newSuggestion) => {
                  // setFieldsValue object notation cannot handle values in nested format
                  const currentValue = getFieldValue('VIDEO_REQUEST')
                  const newValues = {
                    ...currentValue,
                    ...newSuggestion,
                  }
                  setFieldsValue({
                    VIDEO_REQUEST: newValues,
                  })

                  validateFields([fieldName]) // to trigger tracking

                  if (
                    extendedInstructionsActive &&
                    newSuggestion.instructions
                  ) {
                    toggleExtendedInstructions(newSuggestion)
                  }
                }}
                profileId={profileId}
                requestOccasionId={getFieldValue([
                  'VIDEO_REQUEST',
                  'requestOccasionId',
                ])}
                videoRequestDescriptionSuggestionId={getFieldValue([
                  'VIDEO_REQUEST',
                  'videoRequestDescriptionSuggestionId',
                ])}
                textareaStyle={{
                  minHeight: extendedInstructionsActive ? 260 : 160,
                  fontSize: 16,
                }}
                profileProduct={profileProduct}
                placeholder={placeholder}
              />
            </Form.Item>

            {toggleExtendedInstructions && (
              <ExtendedInstructionsAddonComp
                textTooLong={charactersLeft < 0}
                usingSuggestion={Boolean(
                  getFieldValue([
                    'VIDEO_REQUEST',
                    'videoRequestDescriptionSuggestionId',
                  ]),
                )}
                extendedInstructionsProduct={extendedInstructionsProduct}
                extendedInstructionsActive={extendedInstructionsActive}
                toggleExtendedInstructions={toggleExtendedInstructions}
              />
            )}

            {isBusinessProductInternal && (
              <div className={styles.privateCheckbox}>
                <Form.Item
                  name={['VIDEO_REQUEST', 'videoPrivateByCustomer']}
                  valuePropName="checked"
                >
                  <Checkbox data-cy="videoPrivateByCustomerCheckbox">
                    {t(
                      'VideoRequestForm.unlistedCheckboxText',
                      'Private video - do not list it on memmo',
                    )}
                  </Checkbox>
                </Form.Item>
              </div>
            )}
          </>
        )
      }}
    </Form.Item>
  )
}
